import { useMutation, useQueryClient } from 'react-query';

import { api } from '@services';
import { VideosData } from '@common/types/api';

import { useAuth } from '../useAuth';
import { selectUserId } from '../useAuth/selectors';

export const useVideoLikeMutation = (
  videoId: number,
  userHasLiked: boolean,
) => {
  const queryClient = useQueryClient();
  const userId = useAuth(selectUserId);
  const queryKey = ['video-detail', String(videoId)];

  return useMutation(() => api.post(`/videos/${videoId}/like`), {
    onMutate: async () => {
      await queryClient.cancelQueries(queryKey);

      const previousData = queryClient.getQueryData<VideosData>(queryKey);

      if (previousData) {
        const delta = userHasLiked ? -1 : 1;

        const updatedLikes = previousData.likes
          ? { ...previousData.likes }
          : { data: [] };

        if (userHasLiked) {
          updatedLikes.data = updatedLikes.data.filter(
            (like) => like.user_id !== userId,
          );
        } else {
          updatedLikes.data = [
            ...updatedLikes.data,
            {
              id: Date.now(),
              video_id: videoId,
              user_id: userId,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            },
          ];
        }

        queryClient.setQueryData<VideosData>(queryKey, {
          ...previousData,
          likes_count: previousData.likes_count + delta,
          likes: updatedLikes,
        });
      }

      return { previousData };
    },
    onError: (_, __, ctx) => {
      if (ctx?.previousData) {
        queryClient.setQueryData<VideosData>(queryKey, ctx.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(queryKey);
    },
  });
};
